import React from 'react';
import PropTypes from 'prop-types';

import {Box, Typography} from 'views/components';
import {KeyboardArrowDownRounded} from 'views/icons';
import {translate, FilterMenuPopover, Translate} from 'views/module';

import MoreFiltersDropdown from './more_filters/MoreFiltersDropdown';
import Search from './search/Search';

import backend from 'utils/backend';

const FilterBar = (props) => {
  const {
    selection,
    filterOptions,
    reload,
    updateFilter,
  } = props;
  const [anchorEl, setAnchorEl] = React.useState(null);
  const [statusCounts, setStatusCounts] = React.useState({});
  const [isCountLoading, setIsCountLoading] = React.useState(true);

  const statusFilter = filterOptions?.status;
  const selectedValue = selection?.filter?.status?.optionSelected ?? 'ALL';
  const statusMap = translate('client.Status');
  const options = React.useMemo(() => {
    return Object.values(statusFilter?.options ?? {}).sort((a, b) => a.rank - b.rank);
  }, [statusFilter]);
  const pillFilterOptions = options.filter((option) => option.key === 'ALL' || statusMap[option.key]?.primary);
  const moreFilterOptions = options.filter((option) => {
    return option.key !== 'ALL' && !statusMap[option.key]?.primary;
  });

  const COUNTED_STATUS_KEYS = [
    'LEAD',
    'DETAILS_NEEDED',
    'CG_SEARCH',
    'CG_SEARCH_ACTIVE',
    'CG_PROFILE_SENT',
    'MEET_GREET',
  ];


  const trackedStatusKeys = React.useMemo(() => {
    return COUNTED_STATUS_KEYS.filter((key) => statusFilter?.options?.[key]);
  }, [options]);
  const countedStatusKeySet = React.useMemo(() => {
    return new Set(trackedStatusKeys);
  }, [trackedStatusKeys]);
  const selectedStatusKeys = React.useMemo(() => {
    if (Array.isArray(selectedValue)) {
      return selectedValue;
    }

    if (selectedValue && selectedValue !== 'ALL') {
      return [selectedValue];
    }

    return [];
  }, [selectedValue]);
  const isMoreFiltersSelected = Boolean(anchorEl) || moreFilterOptions.some((option) => {
    return selectedStatusKeys.includes(option.key);
  });

  React.useEffect(() => {
    let isActive = true;

    const loadCounts = async () => {
      setIsCountLoading(true);

      if (!statusFilter || !trackedStatusKeys.length) {
        setStatusCounts({});
        setIsCountLoading(false);
        return;
      }

      try {
        const nextCounts = trackedStatusKeys.reduce((acc, key) => {
          acc[key] = 0;
          return acc;
        }, {});
        const trackedKeys = new Set(trackedStatusKeys);
        const sortBy = {
          sortKey: 'createdAt',
          sortDirection: 'DESC',
        };

        let lastEvaluatedKey = null;

        do {
          const response = await backend.client.queryClient([
            {
              key: statusFilter.key,
              value: trackedStatusKeys,
            },
          ], sortBy, lastEvaluatedKey);

          response.list.forEach((item) => {
            const statusKey = item?.[statusFilter.key];

            if (trackedKeys.has(statusKey)) {
              nextCounts[statusKey] += 1;
            }
          });

          lastEvaluatedKey = response?.lastEvaluatedKey;
        } while (lastEvaluatedKey);

        if (!isActive) {
          return;
        }

        setStatusCounts(nextCounts);
      } catch (error) {
        if (!isActive) {
          return;
        }

        setStatusCounts(trackedStatusKeys.reduce((acc, key) => {
          acc[key] = 0;
          return acc;
        }, {}));
      } finally {
        if (isActive) {
          setIsCountLoading(false);
        }
      }
    };

    loadCounts();

    return () => {
      isActive = false;
    };
  }, [reload, statusFilter?.key, trackedStatusKeys]);

  const handleSelectPillStatus = (option) => {
    updateFilter(statusFilter, option);
  };

  const handleToggleMoreFilterStatus = (option) => {
    if (option.key === 'ALL') {
      updateFilter(statusFilter, option);
      return;
    }

    const nextSelectedKeys = [];
    let isSelected = false;

    selectedStatusKeys.forEach((key) => {
      if (key === option.key) {
        isSelected = true;
      } else {
        nextSelectedKeys.push(key);
      }
    });

    if (!isSelected) {
      nextSelectedKeys.push(option.key);
    }

    if (nextSelectedKeys.length === 0) {
      updateFilter(statusFilter, statusFilter.options.ALL);
      return;
    }

    const nextOrderedSelectedKeys = options
        .filter((item) => nextSelectedKeys.includes(item.key))
        .map((item) => item.key);

    updateFilter(statusFilter, {
      key: nextOrderedSelectedKeys,
      label: nextOrderedSelectedKeys.join(', '),
    });
  };

  if (!statusFilter) {
    return null;
  }

  return (
    <Box
      sx={{
        width: '100%',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          alignContent: 'flex-start',
          width: '100%',
          gap: '12px 8px',
        }}
      >
        <Search
          selection={selection}
          filterOptions={filterOptions}
          updateFilter={updateFilter}
        />
        <Box
          sx={{
            width: '1px',
            minWidth: '1px',
            height: '25px',
            backgroundColor: '#E8E4DE',
          }}
        />
        {
          pillFilterOptions.map((option) => {
            const isSelected = option.key === 'ALL' ? selectedStatusKeys.length === 0 : selectedStatusKeys.includes(option.key);
            const shouldShowCount = countedStatusKeySet.has(option.key);
            const count = statusCounts[option.key];
            const optionColors = statusMap[option.key];
            const backgroundColor = isSelected ? (optionColors?.Color ?? '#FFF7F4') : '#F9F8F6';
            const borderColor = isSelected ? (optionColors?.ColorBorder ?? '#EBBDAD') : '#E8E4DE';
            const textColor = isSelected ? (optionColors?.ColorText ?? '#DA7955') : '#6B6760';

            return (
              <React.Fragment key={option.key}>
                <Box
                  onClick={() => handleSelectPillStatus(option)}
                  sx={{
                    'cursor': 'pointer',
                    'display': 'flex',
                    'alignItems': 'center',
                    'justifyContent': 'center',
                    'px': '9px',
                    'py': '4px',
                    'borderRadius': '20px',
                    'border': `1px solid ${borderColor}`,
                    backgroundColor,
                    'whiteSpace': 'nowrap',
                    'transition': 'box-shadow 0.2s ease, background-color 0.2s ease, border-color 0.2s ease',
                    '&:hover': {
                      boxShadow: '0px 0px 0px 3px rgba(218, 121, 85, 0.12)',
                    },
                  }}
                >
                  <Typography
                    sx={{
                      fontFamily: option.key === 'ALL' ? 'Geist Mono' : 'Geist Mono',
                      fontStyle: 'normal',
                      fontWeight: 400,
                      fontSize: '11px',
                      lineHeight: '14px',
                      color: textColor,
                    }}
                  >
                    {shouldShowCount ? `${option.label} ${isCountLoading ? '\u00b7 ...' : `\u00b7 ${count ?? 0}`}` : option.label}
                  </Typography>
                </Box>
              </React.Fragment>
            );
          })
        }
        <Box
          sx={{
            width: '1px',
            minWidth: '1px',
            height: '25px',
            backgroundColor: '#E8E4DE',
          }}
        />
        <Box
          onClick={(event) => setAnchorEl(anchorEl ? null : event.currentTarget)}
          sx={{
            'cursor': 'pointer',
            'display': 'flex',
            'alignItems': 'center',
            'justifyContent': 'center',
            'px': '9px',
            'py': '4px',
            'gap': '4px',
            'borderRadius': '20px',
            'border': `1px solid ${isMoreFiltersSelected ? '#EBBDAD' : '#E8E4DE'}`,
            'backgroundColor': isMoreFiltersSelected ? '#FFF7F4' : '#F9F8F6',
            'whiteSpace': 'nowrap',
            'transition': 'box-shadow 0.2s ease, background-color 0.2s ease, border-color 0.2s ease',
            '&:hover': {
              boxShadow: '0px 0px 0px 3px rgba(218, 121, 85, 0.12)',
            },
          }}
        >
          <Typography
            sx={{
              fontStyle: 'normal',
              fontWeight: 400,
              fontSize: '11px',
              lineHeight: '14px',
              color: isMoreFiltersSelected ? '#DA7955' : '#6B6760',
            }}
          >
            <Translate expression='clientLead.ClientLeadList.Filter.MoreFilters' />
          </Typography>
          <KeyboardArrowDownRounded
            sx={{
              fontSize: '16px',
              color: isMoreFiltersSelected ? '#DA7955' : '#6B6760',
            }}
          />
        </Box>
      </Box>
      {
        anchorEl &&
        <FilterMenuPopover
          anchorEl={anchorEl}
          anchorPositionTop={anchorEl.getBoundingClientRect().bottom + 8}
          anchorPositionLeft={anchorEl.getBoundingClientRect().left}
          onClose={() => setAnchorEl(null)}
          paperSx={{
            boxShadow: '0px 4px 12.1px 0px #0000000D',
          }}
          display={
            <MoreFiltersDropdown
              options={moreFilterOptions}
              selectedKeys={selectedStatusKeys}
              onToggleOption={handleToggleMoreFilterStatus}
            />
          }
        />
      }
    </Box>
  );
};

FilterBar.propTypes = {
  selection: PropTypes.object.isRequired,
  filterOptions: PropTypes.object.isRequired,
  reload: PropTypes.number.isRequired,
  updateFilter: PropTypes.func.isRequired,
};

export default React.memo(FilterBar);
